// AI Service for Contract Clause Generation
import api from './api';
import API_CONFIG from '../config/api';

class AIService {
  // Generate clauses using Gemini AI (OLD - generates full contracts)
  static async generateClauses(prompt, contractType, existingClauses = []) {
    try {
      const response = await api.post('/ai/generate-clauses', {
        prompt,
        contractType,
        existingClauses
      });

      return response.data;
    } catch (error) {
      console.error('Error generating clauses:', error);
      throw error;
    }
  }

  // Generate individual clauses using Gemini AI (NEW - generates usable clauses)
  static async generateClause(context) {
    try {
      const { prompt, contractType, clauseCount = 3 } = context;
      
      const response = await api.post('/ai/generate-individual-clauses', {
        prompt,
        contractType,
        clauseCount
      });

      return response.data;
    } catch (error) {
      console.error('Error generating individual clauses:', error);
      throw error;
    }
  }


  // Analyze existing clauses
  static async analyzeClauses(clauses, contractType) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/analyze-clauses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          clauses,
          contractType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze clauses');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error analyzing clauses:', error);
      throw error;
    }
  }

  // Generate AI contract
  static async generateContract(prompt) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/generate-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate contract');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating contract:', error);
      throw error;
    }
  }

  // Ask AI question
  static async askQuestion(question, context) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/ask-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          question,
          context
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get answer');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error asking question:', error);
      throw error;
    }
  }

  // Intelligent search
  static async intelligentSearch(query, filters) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/intelligent-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          query,
          filters
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to perform search');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error performing intelligent search:', error);
      throw error;
    }
  }

  // Convert natural language to database query
  static async naturalLanguageToQuery(naturalLanguage, context) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/nl-to-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          naturalLanguage,
          context
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to convert to query');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error converting natural language to query:', error);
      throw error;
    }
  }

  // Generate predictive analytics insights
  static async generateInsights(dataType, timeRange, metrics) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/generate-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          dataType,
          timeRange,
          metrics
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate insights');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating insights:', error);
      throw error;
    }
  }

  // Run predictive model
  static async runModel(modelId, parameters) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/run-model`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          modelId,
          parameters
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to run model');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error running model:', error);
      throw error;
    }
  }

  // Process document with AI
  static async processDocument(documentId, options) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/process-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          documentId,
          options
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process document');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error processing document:', error);
      throw error;
    }
  }

  // Extract data from document
  static async extractDocumentData(documentText, documentType) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/extract-document-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          documentText,
          documentType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract document data');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error extracting document data:', error);
      throw error;
    }
  }

  // Analyze clause risk
  static async analyzeClauseRisk(clauseId) {
  try {
    const response = await fetch(
      `${API_CONFIG.API_BASE}/clauses/${clauseId}/analyze`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to analyze clause');
    }

    return await response.json();
  } catch (error) {
    console.error('Error analyzing clause risk:', error);
    throw error;
  }
}

  // Bulk risk scoring
  static async bulkRiskScoring(parameters) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/bulk-risk-scoring`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(parameters)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to perform bulk risk scoring');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error performing bulk risk scoring:', error);
      throw error;
    }
  }

  // Upload audio file for voice-to-contract processing
  static async uploadVoiceAudio(audioBlob, options = {}) {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording-${Date.now()}.webm`);
      if (options.sessionName) formData.append('sessionName', options.sessionName);
      if (options.language) formData.append('language', options.language);
      if (options.contractType) formData.append('contractType', options.contractType);

      const response = await fetch(`${API_CONFIG.API_BASE}/ai/voice/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload audio file');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw error;
    }
  }

  // Transcribe audio file
  static async transcribeVoiceAudio(sessionId) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/voice/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ sessionId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to transcribe audio');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  }

  // Generate contract from voice transcription
  static async generateContractFromVoice(sessionId, options = {}) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/voice/generate-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          sessionId,
          transcription: options.transcription,
          contractType: options.contractType,
          language: options.language,
          jurisdiction: options.jurisdiction
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate contract from voice');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating contract from voice:', error);
      throw error;
    }
  }

  // Get voice sessions
  static async getVoiceSessions(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.skip) params.append('skip', filters.skip);

      const response = await fetch(`${API_CONFIG.API_BASE}/ai/voice/sessions?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch voice sessions');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching voice sessions:', error);
      throw error;
    }
  }
  // ===============================
// Clause Risk Scoring (REQUIRED)
// ===============================

// Fetch all clauses from Clause Library with AI metadata
static async getRiskOverview() {
  try {
    const response = await api.get('/clauses'); 
    return response.data;
  } catch (error) {
    console.error('Error loading clause risk overview:', error);
    throw error;
  }
}

// Analyze a single clause by ID
static async analyzeClause(clauseId) {
  try {
    const response = await api.post(`/clauses/${clauseId}/analyze`);
    return response.data;
  } catch (error) {
    console.error('Error analyzing clause:', error);
    throw error;
  }
}

// Analyze all clauses in Clause Library
static async analyzeAllClauses() {
  try {
    const response = await api.post('/clauses/ai/analyze-all');
    return response.data;
  } catch (error) {
    console.error('Error analyzing all clauses:', error);
    throw error;
  }
}

  // Get single voice session
  static async getVoiceSession(sessionId) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/voice/sessions/${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch voice session');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching voice session:', error);
      throw error;
    }
  }

  // Delete voice session
  static async deleteVoiceSession(sessionId) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/voice/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete voice session');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting voice session:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  static async processVoiceToContract(audioBlob, options) {
    try {
      // Upload audio
      const uploadResponse = await this.uploadVoiceAudio(audioBlob, options);
      if (!uploadResponse.success) {
        throw new Error('Failed to upload audio');
      }

      const sessionId = uploadResponse.data.sessionId;

      // Transcribe
      const transcribeResponse = await this.transcribeVoiceAudio(sessionId);
      if (!transcribeResponse.success) {
        throw new Error('Failed to transcribe audio');
      }

      // Generate contract
      const contractResponse = await this.generateContractFromVoice(sessionId, {
        contractType: options.contractType,
        language: options.language
      });

      return {
        success: true,
        sessionId,
        transcription: transcribeResponse.data.transcription,
        contract: contractResponse.data.contractContent,
        accuracy: transcribeResponse.data.confidence,
        sessionName: uploadResponse.data.sessionName,
        contractType: contractResponse.data.extractedData?.contractType || options.contractType,
        participants: contractResponse.data.extractedData?.parties || [],
        keyPoints: contractResponse.data.extractedData?.keyPoints || [],
        processingTime: transcribeResponse.data.processingTime
      };
    } catch (error) {
      console.error('Error processing voice to contract:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  static async generateContractFromText(text, options) {
    try {
      const response = await this.generateContractFromVoice(null, {
        transcription: text,
        contractType: options.contractType,
        language: options.language,
        jurisdiction: options.jurisdiction
      });

      return {
        success: true,
        contract: response.data.contractContent
      };
    } catch (error) {
      console.error('Error generating contract from text:', error);
      throw error;
    }
  }

  // Translate text
  static async translateText(text, options) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          text,
          options
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to translate text');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error translating text:', error);
      throw error;
    }
  }

  // Analyze negotiation strategy
  static async analyzeNegotiationStrategy(parameters) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/analyze-negotiation-strategy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(parameters)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze negotiation strategy');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error analyzing negotiation strategy:', error);
      throw error;
    }
  }

  // Get negotiation advice
  static async getNegotiationAdvice(question, context) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/get-negotiation-advice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          question,
          context
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get negotiation advice');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting negotiation advice:', error);
      throw error;
    }
  }

  // Check AI service health
  static async checkHealth() {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/health`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking AI service health:', error);
      return { success: false, error: error.message };
    }
  }

  // Extract clauses from uploaded document (simulated)
  static async extractClausesFromDocument(file) {
    try {
      // In a real implementation, this would upload the file to the backend
      // and use document processing services
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_CONFIG.API_BASE}/ai/extract-clauses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract clauses from document');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error extracting clauses from document:', error);
      
      // Fallback: simulate clause extraction
      return {
        success: true,
        clauses: [
          {
            id: `extracted_${Date.now()}_1`,
            title: 'Payment Terms from Document',
            content: 'Payment shall be made within 30 days of invoice receipt. Late payments may incur interest at 1.5% per month.',
            category: 'Financial',
            isRequired: true,
            type: 'Standard',
            source: 'uploaded_file',
            extractedAt: new Date().toISOString()
          },
          {
            id: `extracted_${Date.now()}_2`,
            title: 'Termination Clause from Document',
            content: 'Either party may terminate this agreement with 60 days written notice.',
            category: 'Termination',
            isRequired: false,
            type: 'Standard',
            source: 'uploaded_file',
            extractedAt: new Date().toISOString()
          }
        ]
      };
    }
  }

  // Get AI contract statistics
  static async getContractStats() {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/contract-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch contract statistics');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching contract statistics:', error);
      throw error;
    }
  }

  // Get generation analytics
  static async getGenerationAnalytics(timeframe = '30d') {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/generation-analytics?timeframe=${timeframe}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch generation analytics');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching generation analytics:', error);
      throw error;
    }
  }

  // Generate contract using AI
  static async generateContract(prompt, contractType) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/generate-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          prompt,
          contractType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate contract');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating contract:', error);
      throw error;
    }
  }

  // Get AI Search & Q&A statistics
  static async getSearchStats() {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/search-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch search statistics');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching search statistics:', error);
      throw error;
    }
  }

  // Get AI Search & Q&A analytics
  static async getSearchAnalytics(timeframe = '30d') {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/search-analytics?timeframe=${timeframe}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch search analytics');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching search analytics:', error);
      throw error;
    }
  }

  // Get AI Translation statistics
  static async getTranslationStats() {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch translation stats');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching translation stats:', error);
      throw error;
    }
  }

  // Get AI Translation analytics
  static async getTranslationAnalytics(timeframe = '30d') {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation-analytics?timeframe=${timeframe}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch translation analytics');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching translation analytics:', error);
      throw error;
    }
  }

  // Get chat history
  static async getChatHistory(category = 'all', search = '', limit = 50, skip = 0) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/chat-history?category=${category}&search=${search}&limit=${limit}&skip=${skip}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch chat history');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching chat history:', error);
      throw error;
    }
  }

  // Save chat conversation
  static async saveChatHistory(question, answer, category, tags, summary) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/chat-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          question,
          answer,
          category,
          tags,
          summary
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save chat history');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving chat history:', error);
      throw error;
    }
  }

  // Delete chat conversation
  static async deleteChatHistory(chatId) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/chat-history/${chatId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete chat history');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting chat history:', error);
      throw error;
    }
  }

  // Get AI chat settings
  static async getChatSettings() {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/chat-settings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch chat settings');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching chat settings:', error);
      throw error;
    }
  }

  // Update AI chat settings
  static async updateChatSettings(settings) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/chat-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update chat settings');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating chat settings:', error);
      throw error;
    }
  }

  // Generate AI contract from form data
  static async generateContract(formData) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/generate-contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate contract');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating contract:', error);
      throw error;
    }
  }

  // Generate AI-powered suggestions
  static async generateSuggestions(categories = ['contracts', 'assets', 'processes']) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/generate-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ categories })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate suggestions');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error generating suggestions:', error);
      throw error;
    }
  }

  // Chat with Gemini AI
  static async chatWithGemini(message, context) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/gemini`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message,
          context: context || "You are CAM360 AI assistant, a specialized AI for contract and asset management. Help users with questions about contracts, assets, maintenance, compliance, and business processes. Provide helpful, accurate, and professional responses."
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get response from AI');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error chatting with Gemini:', error);
      throw error;
    }
  }

  // Translate text using AI (updated to use new endpoint)
  static async translateText(text, sourceLanguage, targetLanguage, documentType = 'Document') {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          text,
          sourceLanguage,
          targetLanguage,
          documentType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to translate text');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error translating text:', error);
      throw error;
    }
  }

  // Translation methods
  static async translateText(text, sourceLanguage, targetLanguage, documentType = 'Document') {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          text,
          sourceLanguage,
          targetLanguage,
          documentType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to translate text');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error translating text:', error);
      throw error;
    }
  }

  static async getTranslationStats() {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch translation statistics');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching translation stats:', error);
      throw error;
    }
  }

  static async getTranslationHistory(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.sourceLanguage) params.append('sourceLanguage', filters.sourceLanguage);
      if (filters.targetLanguage) params.append('targetLanguage', filters.targetLanguage);
      if (filters.status) params.append('status', filters.status);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.skip) params.append('skip', filters.skip);

      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation/history?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch translation history');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching translation history:', error);
      throw error;
    }
  }

  static async deleteTranslation(translationId) {
    try {
      const response = await fetch(`${API_CONFIG.API_BASE}/ai/translation/${translationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete translation');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting translation:', error);
      throw error;
    }
  }
}

export default AIService;
